import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { EmailAccountForm, useFormatDate } from "@/components/email-account-form";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { Heading, Text } from "@/components/ui";
import { readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type FailedEmail = {
  id: string;
  toEmail: string;
  template: string;
  error: string | null;
  attempts: number;
  createdAt: string;
  lastAttemptAt: string | null;
};

const TEMPLATE_KEYS: Record<string, string> = {
  "enrollment.receipt": "enrollment_receipt",
  "auth.verify_email": "auth_verify_email",
  "auth.password_reset": "auth_password_reset",
  // MT-1 INFRA-024 S5b: a reset request that waited too long for the school's account.
  "auth.password_reset_request": "auth_password_reset_request",
  "auth.account_welcome": "auth_account_welcome",
  "auth.account_welcome_request": "auth_account_welcome",
  "auth.otp_code": "auth_otp_code",
};

/**
 * COMM-1: emails that did not go out in the last 30 days — a missing sender, an unverified domain,
 * a rejected key — so the admin who can fix it sees it.
 */
function FailedEmails() {
  const t = useTranslations("emailSettings");
  const formatDate = useFormatDate();
  const [failures, setFailures] = useState<FailedEmail[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch("/v1/tenant/email-settings/failures");
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as { data: { failures: FailedEmail[] } };
        setFailures(json.data.failures);
      } catch {
        setLoadFailed(true);
      }
    })();
  }, []);

  return (
    <section className="space-y-3" data-testid="email-settings-failures">
      <Heading as="h2" level="section">
        {t("failedTitle")}
      </Heading>
      <Text tone="muted">{t("failedHint")}</Text>
      {loadFailed ? (
        <Text tone="muted">{t("failedLoadFailed")}</Text>
      ) : failures === null ? (
        <Text tone="muted">{t("loading")}</Text>
      ) : failures.length === 0 ? (
        <Text tone="muted" data-testid="email-settings-failures-empty">
          {t("failedEmpty")}
        </Text>
      ) : (
        <ul className="space-y-2">
          {failures.map((failure) => (
            <li
              key={failure.id}
              className="rounded border border-[var(--border)] p-3 text-sm"
              data-testid={`email-failure-${failure.id}`}
            >
              <p className="font-medium">
                {t(`templates.${TEMPLATE_KEYS[failure.template] ?? "other"}`)} — {failure.toEmail}
              </p>
              <p className="text-[var(--text-muted)]">
                {formatDate(failure.lastAttemptAt ?? failure.createdAt)} ·{" "}
                {t("failedAttempts", { count: failure.attempts })}
              </p>
              {failure.error ? <p className="mt-1 break-words">{failure.error}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * INFRA-006: the school admin's email settings page — the shared account form (MT-1 INFRA-024 S1,
 * `email-account-form.tsx`) and the school's failed emails.
 */
export function TenantEmailSettings() {
  const t = useTranslations("emailSettings");
  const [loaded, setLoaded] = useState(false);
  const { tenant: school } = useSchoolBootstrap();
  return (
    <section className="space-y-6" data-testid="tenant-email-settings">
      <header className="space-y-2">
        <Heading as="h1" level="page">
          {t("title")}
        </Heading>
        <Text tone="muted">{t("intro")}</Text>
      </header>
      <EmailAccountForm
        endpoint="/v1/tenant/email-settings"
        fetcher={apiFetch}
        onLoaded={() => setLoaded(true)}
        schoolName={school?.name}
      />
      {loaded ? <FailedEmails /> : null}
    </section>
  );
}
